import { Tour } from 'antd';
import type { TourProps } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { askAssistant } from 'redux/slice/chatBubbleSlice';
import { AppDispatch } from 'redux/store';
import useAiAssistantAccess from 'utils/useAiAssistantAccess';
import TutorialComplete from './TutorialComplete';
import { useTutorial } from './TutorialContext';
import { TutorialStep } from './types';
import './index.scss';

/** How long to wait for a step's target to mount before giving up on it. */
const TARGET_TIMEOUT_MS = 4000;
const TARGET_POLL_MS = 100;
/**
 * How long the panel stays hidden while a target is being located. Long enough
 * to cover a route change so the panel never renders against the previous step's
 * position, short enough that a slow-loading target shows the step centred
 * instead of looking like the tutorial died.
 */
const TARGET_GRACE_MS = 600;

/**
 * True when the browser is already on the route a step wants.
 *
 * Only the params the step actually names are compared — pages here carry other
 * params (payPeriodId, openDrawer) that must not count as a mismatch, otherwise
 * the tour would navigate in a loop.
 */
const isOnStepRoute = (step: TutorialStep, pathname: string, search: string) => {
	const [routePath, routeQuery = ''] = step.route.split('?');
	if (routePath !== pathname) {
		return false;
	}
	const currentParams = new URLSearchParams(search);
	const requiredParams = new URLSearchParams(routeQuery);
	let matches = true;
	requiredParams.forEach((value, key) => {
		if (currentParams.get(key) !== value) {
			matches = false;
		}
	});
	return matches;
};

/**
 * The URL to navigate to for a step.
 *
 * Params already in the URL are kept when the step stays on the same page —
 * switching the Cost Allocations tab must not drop the selected payPeriodId —
 * but are dropped when moving to a different page, where they mean nothing.
 */
const buildStepUrl = (step: TutorialStep, pathname: string, search: string) => {
	const [routePath, routeQuery = ''] = step.route.split('?');
	const nextParams =
		routePath === pathname
			? new URLSearchParams(search)
			: new URLSearchParams();
	new URLSearchParams(routeQuery).forEach((value, key) => {
		nextParams.set(key, value);
	});
	const queryString = nextParams.toString();
	return queryString ? `${routePath}?${queryString}` : routePath;
};

/**
 * Splits the steps into one group per page, in order.
 *
 * The progress header renders the group you're currently in as a dot per step
 * and every other group as a plain bar, so a glance says both "which page of the
 * walkthrough" and "how far through this page".
 */
const groupStepsByPage = (steps: TutorialStep[]) => {
	const groups: number[][] = [];
	let previousPage: string | null = null;
	steps.forEach((step, index) => {
		const page = step.route.split('?')[0];
		if (page === previousPage) {
			groups[groups.length - 1].push(index);
		} else {
			groups.push([index]);
			previousPage = page;
		}
	});
	return groups;
};

/** Sparkle glyph for the assistant hand-off; antd has no equivalent. */
const SparkleIcon = () => (
	<svg
		width="13"
		height="13"
		viewBox="0 0 24 24"
		fill="currentColor"
		aria-hidden="true"
	>
		<path d="M12 2.5l1.9 5.1 5.1 1.9-5.1 1.9L12 16.5l-1.9-5.1L5 9.5l5.1-1.9L12 2.5Z" />
		<path d="M18.5 15l.9 2.3 2.3.9-2.3.9-.9 2.4-.9-2.4-2.3-.9 2.3-.9.9-2.3Z" />
	</svg>
);

/**
 * Drives whichever tutorial the TutorialProvider says is running: navigates to
 * each step's page, waits for its target to exist, then hands the resolved
 * element to antd's Tour.
 */
const TutorialTour = () => {
	const {
		activeTutorial,
		steps,
		currentStepIndex,
		goToStep,
		exitTutorial,
		finishTutorial,
	} = useTutorial();
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const dispatch = useDispatch<AppDispatch>();
	const hasAiAssistantAccess = useAiAssistantAccess();

	const [target, setTarget] = useState<HTMLElement | null>(null);
	// Keeps the Tour closed while a step's target is still being located, so the
	// panel never renders against the previous step's position or flashes in the
	// middle of the screen on the way to the right element.
	const [isLocatingTarget, setIsLocatingTarget] = useState(false);

	const step: TutorialStep | undefined = steps[currentStepIndex];
	const isOnRoute = step ? isOnStepRoute(step, pathname, search) : false;

	// Navigating inside an effect (rather than in the click handler) keeps this
	// correct for every way the step can change: Next, Back, and the progress dots.
	const navigateRef = useRef(navigate);
	navigateRef.current = navigate;
	// Each step gets one navigation attempt. Without this, a step whose page
	// bounces the user elsewhere — /cost-allocations redirects to /unauthorized
	// without the permission — would never satisfy isOnRoute, and the effect would
	// navigate straight back into a loop.
	const navigatedForStepRef = useRef<string | null>(null);
	useEffect(() => {
		if (!step) {
			navigatedForStepRef.current = null;
			return;
		}
		if (isOnRoute) {
			navigatedForStepRef.current = step.id;
			return;
		}
		if (navigatedForStepRef.current === step.id) {
			return;
		}
		navigatedForStepRef.current = step.id;
		navigateRef.current(buildStepUrl(step, pathname, search));
	}, [step, isOnRoute, pathname, search]);

	useEffect(() => {
		if (!step) {
			setTarget(null);
			setIsLocatingTarget(false);
			return;
		}

		let pollId: ReturnType<typeof setTimeout>;
		let cancelled = false;
		const deadline = Date.now() + TARGET_TIMEOUT_MS;

		setTarget(null);
		setIsLocatingTarget(true);

		// Stop hiding the panel after the grace window even if nothing was found
		// yet; the poll below keeps running and re-anchors it if the element turns
		// up later.
		const graceId = setTimeout(() => {
			if (!cancelled) {
				setIsLocatingTarget(false);
			}
		}, TARGET_GRACE_MS);

		// Targets appear late for ordinary reasons — the page is still fetching its
		// employee costs, or an antd Tabs panel hasn't mounted yet — so poll rather
		// than read the DOM once.
		const locate = () => {
			if (cancelled) {
				return;
			}
			// Nothing to query until the browser is on the step's page; the effect
			// re-runs once it gets there.
			const element = isOnRoute
				? document.querySelector<HTMLElement>(step.target)
				: null;
			if (element) {
				setTarget(element);
				setIsLocatingTarget(false);
				return;
			}
			if (Date.now() >= deadline) {
				// Give up quietly and leave the step centred rather than stalling the
				// tour. The element is genuinely absent for this user — an action they
				// lack permission for, or a table with no rows yet — and the copy
				// still explains the step.
				setIsLocatingTarget(false);
				return;
			}
			pollId = setTimeout(locate, TARGET_POLL_MS);
		};
		locate();

		return () => {
			cancelled = true;
			clearTimeout(pollId);
			clearTimeout(graceId);
		};
	}, [step, isOnRoute]);

	const pageGroups = useMemo(() => groupStepsByPage(steps), [steps]);

	const tourSteps = useMemo<TourProps['steps']>(
		() =>
			steps.map((singleStep, index) => {
				// Only the current step is ever rendered by antd, so the other targets
				// are never read — and must not be, since they live on other pages.
				const resolvedTarget = index === currentStepIndex ? target : null;
				// The assistant now lives only on the /chat page, so handing a
				// question over means stepping out of the tour and navigating there
				// rather than opening a bubble over the tour overlay.
				const askAi = () => {
					dispatch(askAssistant(singleStep.aiPrompt));
					exitTutorial();
					navigate('/chat');
				};
				return {
					title: (
						<span className="cap-tutorial-title">
							<span className="cap-tutorial-title__icon">
								{singleStep.icon}
							</span>
							{singleStep.title}
						</span>
					),
					description: (
						<>
							{singleStep.description}
							{hasAiAssistantAccess && (
								<button
									type="button"
									className="cap-tutorial-ask-ai"
									onClick={askAi}
								>
									<SparkleIcon />
									Ask AI about this step
								</button>
							)}
						</>
					),
					placement: singleStep.placement,
					// antd renders `cover` above the title — the progress header sits
					// there rather than in the default dot row at the bottom.
					cover: (
						<div className="cap-tutorial-progress">
							{pageGroups.map((group) => {
								const isCurrentPage = group.includes(currentStepIndex);
								const key = `page-${group[0]}`;
								if (!isCurrentPage) {
									const isDone = group[group.length - 1] < currentStepIndex;
									return (
										<span
											key={key}
											className={`cap-tutorial-progress__line${
												isDone ? ' cap-tutorial-progress__line--done' : ''
											}`}
										/>
									);
								}
								return (
									<span key={key} className="cap-tutorial-progress__dots">
										{group.map((stepIndex) => {
											let state = '';
											if (stepIndex === currentStepIndex) {
												state = ' cap-tutorial-progress__dot--active';
											} else if (stepIndex < currentStepIndex) {
												state = ' cap-tutorial-progress__dot--done';
											}
											return (
												<span
													key={stepIndex}
													className={`cap-tutorial-progress__dot${state}`}
												/>
											);
										})}
									</span>
								);
							})}
							<span className="cap-tutorial-progress__label">
								Step {index + 1} of {steps.length}
							</span>
						</div>
					),
					// No target => antd centres the panel, which is the fallback for a
					// step whose element this user doesn't have.
					target: resolvedTarget ? () => resolvedTarget : undefined,
				};
			}),
		[
			steps,
			pageGroups,
			currentStepIndex,
			target,
			hasAiAssistantAccess,
			dispatch,
			exitTutorial,
		]
	);

	// The completion card outlives the tour — it is what the last Next hands off
	// to — so it renders here rather than behind the early return below.
	if (!activeTutorial || !step) {
		return <TutorialComplete />;
	}

	const isLastStep = currentStepIndex === steps.length - 1;

	return (
		<Tour
			open={!isLocatingTarget}
			current={currentStepIndex}
			steps={tourSteps}
			rootClassName="cap-tutorial-tour"
			// The footer carries an explicit "Skip tutorial", so antd's corner ✕
			// would just be a second way to do the same thing.
			closable={false}
			// The progress header above replaces antd's dot row entirely.
			indicatorsRender={() => null}
			mask={{ color: 'rgba(23, 32, 24, 0.55)' }}
			gap={{ offset: 6, radius: 10 }}
			onChange={goToStep}
			onClose={exitTutorial}
			onFinish={finishTutorial}
			scrollIntoViewOptions={{ block: 'center', behavior: 'smooth' }}
			// Replaces antd's Button pair so the controls match the app rather than
			// antd's defaults.
			actionsRender={() => (
				<div className="cap-tutorial-actions">
					<button
						type="button"
						className="cap-tutorial-actions__skip"
						onClick={exitTutorial}
					>
						Skip tutorial
					</button>
					<div className="cap-tutorial-actions__right">
						{currentStepIndex > 0 && (
							<button
								type="button"
								className="cap-tutorial-actions__back"
								onClick={() => goToStep(currentStepIndex - 1)}
							>
								Back
							</button>
						)}
						<button
							type="button"
							className="cap-tutorial-actions__next"
							onClick={() =>
								isLastStep
									? finishTutorial()
									: goToStep(currentStepIndex + 1)
							}
						>
							{isLastStep ? 'Finish' : 'Next'}
						</button>
					</div>
				</div>
			)}
		/>
	);
};

export default TutorialTour;
